import React from "react";

import { campaignApi, cancelSource, isCancelled, uuid } from "api/aiCampaigns";

import {
  LANGUAGES,
  SEND_DAYS,
  STAGES,
  STAGE_CHANNELS,
  describeError,
  fieldErrorMessage,
  isSriLankanMobile,
  metadata,
  overlongSms,
  pendingCampaign
} from "../campaignUtils";
import { getConfirmation } from "../shared/confirmations";
import { ConfirmModal } from "../shared/StudioControls";
import { CampaignDetails } from "./CampaignDetails";
import {
  allChannelsTested,
  getCampaignAction,
  getCurrentCampaignStep,
  hasCompleteContent
} from "./campaignDetails.utils";

const BASE_DELAY = 15000;
// Background AI jobs publish step-by-step progress; check it more often while one runs.
const JOB_DELAY = 5000;
const OUTCOMES_DELAY = 300000;
const emptySettings = () => ({ channels: ["sms"], language_mode: "preferred", languages: [] });
const clone = value => JSON.parse(JSON.stringify(value));
const submittedTestChannels = (logs, revision) =>
  logs.filter(log => metadata(log).test && metadata(log).revision === revision && log.status === "submitted").map(log => log.channel);

// Owns every read, poll and mutation for one campaign. CampaignDetails and its
// step components are presentational.
export class CampaignDetailsContainer extends React.Component {
  state = {
    workspace: null,
    campaign: null,
    counts: [],
    outcomes: null,
    outcomeWindow: 7,
    outcomesLoading: false,
    outcomesError: "",
    audience: null,
    logs: null,
    preview: null,
    loading: true,
    busy: false,
    error: "",
    readError: "",
    notice: "",
    forbidden: false,
    stale: false,
    settings: emptySettings(),
    content: [],
    dirty: false,
    feedback: "",
    stage: STAGES[0],
    channel: "sms",
    language: "English",
    lead: "",
    destination: "",
    reviewed: false,
    received: false,
    deliveryChannels: [],
    deliveryLanguages: [],
    uncertain: null,
    supersededTest: null,
    tested: [],
    confirm: null,
    audienceConfirmed: false,
    contentConfirmedRevision: null,
    testStepDoneRevision: null,
    testFieldError: "",
    selectedDay: null
  };
  sources = new Set();
  alive = false;
  delay = BASE_DELAY;
  readFailed = false;
  readVersion = 0;
  outcomesVersion = 0;
  outcomesFetchedAt = 0;

  componentDidMount() {
    this.alive = true;
    const search = this.props.history && this.props.history.location && this.props.history.location.search;
    const params = new URLSearchParams(search || "");
    this.audienceList = params.get("list") === "six-day" ? "six-day" : "lead-list";
    const day = Number(params.get("day"));
    this.selectedDay = SEND_DAYS.includes(day) ? day : null;
    if (this.selectedDay) {
      const index = SEND_DAYS.indexOf(this.selectedDay);
      this.setState({ stage: STAGES[index], channel: STAGE_CHANNELS[index], selectedDay: this.selectedDay });
    }
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    if (this.props.history) {
      this.unblock = this.props.history.block(location => {
        if (!this.state.dirty) return undefined;
        this.setState({
          confirm: {
            ...getConfirmation("leave"),
            run: () => this.setState({ dirty: false }, () => this.props.history.push(location))
          }
        });
        return false;
      });
    }
    this.refresh();
  }

  componentWillUnmount() {
    this.alive = false;
    this.outcomesVersion++;
    clearTimeout(this.timer);
    this.sources.forEach(source => source.cancel());
    if (this.unblock) this.unblock();
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  }

  handleBeforeUnload = event => {
    if (this.state.dirty) {
      event.preventDefault();
      event.returnValue = "";
    }
  };

  call = async (fn, ...args) => {
    const source = cancelSource();
    this.sources.add(source);
    try {
      return await fn(this.props.token, ...args, source.token);
    } finally {
      this.sources.delete(source);
    }
  };

  acceptCampaign = campaign => {
    const payload = campaign.payload || {};
    const previous = this.state.campaign;
    const same = !!previous && previous.id === campaign.id;
    const revisionChanged = same && previous.payload.revision !== payload.revision;
    const statusChanged = same && previous.status !== campaign.status;
    const patch = { campaign };

    if (same && this.state.uncertain && this.state.uncertain.revision !== payload.revision) {
      // An uncertain test on an older revision cannot be retried against new content.
      patch.supersededTest = this.state.uncertain;
      patch.uncertain = null;
      patch.destination = "";
    }
    if (same && !this.state.dirty && campaign.status === "generating" && !statusChanged && !revisionChanged) {
      // Each finished message is saved as the job runs: show it without moving the admin's tabs.
      patch.settings = clone(payload.settings || emptySettings());
      patch.content = clone(payload.content || []);
    } else if (!same || (!this.state.dirty && (revisionChanged || statusChanged || !this.state.content.length))) {
      patch.settings = clone(payload.settings || emptySettings());
      patch.content = clone(payload.content || []);
      patch.dirty = false;
      patch.stale = false;
      patch.channel = patch.settings.channels[0] || "sms";
      patch.destination = "";
      patch.testFieldError = "";
      patch.language = (patch.settings.language_mode === "preferred" ? LANGUAGES : patch.settings.languages)
        .find(language => LANGUAGES.includes(language)) || "English";
    }
    if (revisionChanged || !same) {
      Object.assign(patch, { preview: null, reviewed: false, received: false, deliveryChannels: [], deliveryLanguages: [], tested: [] });
    }
    if (revisionChanged && this.state.dirty) {
      Object.assign(patch, {
        stale: true,
        error: "Another admin saved a newer revision. Your draft is kept below. Discard it to load the latest version before saving."
      });
    }
    this.setState(patch);
  };

  schedule = () => {
    clearTimeout(this.timer);
    const { campaign, logs, forbidden, stale, uncertain } = this.state;
    if (!this.alive || forbidden) return;
    const testing = uncertain || (logs && logs.data.some(log => ["queued", "processing"].includes(log.status)));
    if (this.readFailed || pendingCampaign(campaign) || testing) {
      const generating = campaign && campaign.status === "generating";
      this.timer = setTimeout(this.refresh, stale ? Math.max(this.delay, 30000) : generating ? Math.min(this.delay, JOB_DELAY) : this.delay);
    }
  };

  refresh = async () => {
    if (this.refreshing || this.state.busy) return;
    clearTimeout(this.timer);
    this.refreshing = true;
    const version = this.readVersion;
    const { campaignId } = this.props;
    try {
      const [workspace, detail, logs] = await Promise.all([
        this.call(campaignApi.getWorkspace, 1),
        this.call(campaignApi.getCampaign, campaignId),
        this.call(campaignApi.getLogs, campaignId, this.state.logs ? this.state.logs.current_page : 1)
      ]);
      if (!this.alive || version !== this.readVersion) return;
      const campaign = detail.data.campaign;
      const needsAudience = !this.state.audience;
      this.setState({ workspace: workspace.data });
      this.acceptCampaign(campaign);
      const newTests = submittedTestChannels(logs.data.data, campaign.payload.revision);
      this.setState(state => ({
        counts: detail.data.delivery_counts || [],
        logs: logs.data,
        tested: Array.from(new Set(state.tested.concat(newTests)))
      }));
      if (Date.now() - this.outcomesFetchedAt >= OUTCOMES_DELAY) this.loadOutcomes(this.state.outcomeWindow);
      if (campaign.payload.audience_ready && needsAudience) await this.loadPage("audience", 1, true);
      this.delay = Math.min(this.delay * 1.3, 30000);
      this.readFailed = false;
      this.setState({ readError: "", forbidden: false });
    } catch (error) {
      if (!this.alive || isCancelled(error) || version !== this.readVersion) return;
      const described = describeError(error);
      this.readFailed = true;
      this.delay = described.status === 429 ? 60000 : Math.min(this.delay * 2, 60000);
      this.setState({ readError: described.message, forbidden: described.forbidden });
    } finally {
      this.refreshing = false;
      if (this.alive && version === this.readVersion) this.setState({ loading: false }, this.schedule);
    }
  };

  loadOutcomes = async windowDays => {
    const version = ++this.outcomesVersion;
    this.outcomesFetchedAt = Date.now();
    this.setState({ outcomesLoading: true, outcomesError: "" });
    try {
      const response = await this.call(campaignApi.getOutcomes, this.props.campaignId, windowDays);
      if (this.alive && version === this.outcomesVersion) {
        this.setState({ outcomes: response.data, outcomesLoading: false });
      }
    } catch (error) {
      if (this.alive && version === this.outcomesVersion && !isCancelled(error)) {
        this.setState({ outcomes: null, outcomesLoading: false, outcomesError: describeError(error).message });
      }
    }
  };

  handleOutcomeWindowChange = windowDays => {
    if (windowDays === this.state.outcomeWindow) return;
    this.setState({ outcomeWindow: windowDays, outcomes: null }, () => this.loadOutcomes(windowDays));
  };

  retryOutcomes = () => this.loadOutcomes(this.state.outcomeWindow);

  loadPage = async (kind, page, propagate = false) => {
    const fn = kind === "audience" ? campaignApi.getAudience : campaignApi.getLogs;
    try {
      const response = kind === "audience"
        ? await this.call(fn, this.props.campaignId, page, this.selectedDay)
        : await this.call(fn, this.props.campaignId, page);
      if (!this.alive) return;
      this.setState(state => ({
        [kind]: response.data,
        tested: kind === "logs" && state.campaign
          ? Array.from(new Set(state.tested.concat(submittedTestChannels(response.data.data, state.campaign.payload.revision))))
          : state.tested
      }));
    } catch (error) {
      if (propagate) throw error;
      if (!this.alive || isCancelled(error)) return;
      this.setState({ error: describeError(error).message });
    }
  };

  handleAudienceListChange = (list, day = null) => {
    this.audienceList = list;
    this.selectedDay = list === "six-day" ? (day || this.selectedDay || 1) : null;
    const stageIndex = this.selectedDay ? SEND_DAYS.indexOf(this.selectedDay) : -1;
    this.setState({
      audience: null,
      selectedDay: this.selectedDay,
      stage: stageIndex >= 0 ? STAGES[stageIndex] : this.state.stage,
      channel: stageIndex >= 0 ? STAGE_CHANNELS[stageIndex] : this.state.channel,
      error: ""
    }, () => {
      if (this.props.history) {
        const query = list === "six-day" ? `?list=six-day&day=${this.selectedDay}` : "?list=lead-list";
        this.props.history.replace(`${this.props.basePath}/${this.props.campaignId}${query}`);
      }
      this.loadPage("audience", 1);
    });
  };

  mutate = async (fn, args, options = {}) => {
    if (options.sendsMessages && overlongSms(this.state.content, this.state.settings).length) {
      this.setState({ error: "SMS sending is blocked. Shorten every selected SMS message to 160 characters or fewer." });
      return;
    }
    if (this.state.busy || this.mutating) return;
    this.mutating = true;
    this.delay = BASE_DELAY;
    clearTimeout(this.timer);
    this.readVersion++;
    this.setState({ busy: true, error: "", notice: "", testFieldError: "" });
    try {
      const response = await this.call(fn, ...args);
      if (!this.alive) return;
      if (options.test) {
        const body = options.test;
        this.setState(state => ({
          uncertain: null,
          logs: null,
          notice: `${response.message} Status: ${response.data.status}.`,
          tested: response.data.status === "submitted" ? Array.from(new Set(state.tested.concat(body.channel))) : state.tested
        }));
      } else {
        this.setState({ notice: response.message || "" });
      }
      if (options.clearDraft) this.setState({ dirty: false, content: [], reviewed: false, received: false, deliveryChannels: [], deliveryLanguages: [], preview: null });
      if (options.onSuccess) options.onSuccess(response);
    } catch (error) {
      if (!this.alive || isCancelled(error)) return;
      const described = describeError(error);
      const status = error.response && error.response.status;
      if (options.test && [400, 404, 422].includes(status)) {
        const destinationField = { email: "email", sms: "phone", push: "mobile_no" }[options.test.channel];
        // Validation and push lead/device lookup failures appear beside the destination field.
        this.setState({ testFieldError: fieldErrorMessage(error, destinationField) || described.message });
        return;
      }
      this.setState({ error: described.message, forbidden: described.forbidden });
      if (options.test && (!error.response || error.response.status >= 500)) {
        this.setState({
          uncertain: options.test,
          notice: "The test outcome is uncertain. Check the history before retrying. Retrying resends the identical request, so no duplicate is created."
        });
      }
      if (error.response && error.response.status === 409 && this.state.dirty) this.setState({ stale: true });
    } finally {
      this.mutating = false;
      if (this.alive) {
        this.setState({ busy: false }, () => {
          this.refreshing = false;
          this.refresh();
        });
      }
    }
  };

  // ----- Content -----
  handleSettingsChange = (key, value) => this.setState(state => ({
    settings: { ...state.settings, [key]: value },
    dirty: true,
    reviewed: false,
    received: false,
    preview: null
  }));

  handleEdit = (key, value) => this.setState(state => {
    const content = clone(state.content);
    content[STAGES.indexOf(state.stage)].variants[state.channel][state.language][key] = value;
    return { content, dirty: true, reviewed: false, received: false, preview: null };
  });

  handleGenerate = () => {
    const { campaign, settings, feedback } = this.state;
    this.setState({
      confirm: {
        ...getConfirmation("generate", { hasContent: hasCompleteContent(campaign.payload) }),
        run: () => this.mutate(
          campaignApi.generateContent,
          [campaign.id, { revision: campaign.payload.revision, ...settings, feedback }],
          { clearDraft: true }
        )
      }
    });
  };

  handleRetryGeneration = () => {
    const { campaign } = this.state;
    this.mutate(campaignApi.retryGeneration, [campaign.id], { clearDraft: true });
  };

  handleSave = () => {
    const { campaign, content } = this.state;
    this.mutate(campaignApi.saveContent, [campaign.id, { revision: campaign.payload.revision, content }], { clearDraft: true });
  };

  handleDiscard = () => this.setState({
    confirm: {
      ...getConfirmation("discardEdits"),
      run: () => this.setState({ dirty: false, stale: false, content: [], error: "" }, this.refresh)
    }
  });

  handleConfirmAudience = () => this.setState({ audienceConfirmed: true });

  handleFinishTesting = () => this.setState(state => ({ testStepDoneRevision: state.campaign.payload.revision }));

  handleConfirmContent = () => this.setState(state => ({ contentConfirmedRevision: state.campaign.payload.revision }));

  handlePreview = async () => {
    const { campaignId } = this.props;
    this.setState({ busy: true, error: "", preview: null });
    try {
      const response = await this.call(campaignApi.getPreview, campaignId, this.state.lead);
      if (this.alive) this.setState({ preview: response.data });
    } catch (error) {
      if (this.alive && !isCancelled(error)) this.setState({ error: describeError(error).message });
    } finally {
      if (this.alive) this.setState({ busy: false }, this.schedule);
    }
  };

  // ----- Test, approve, deliver -----
  handleSendTest = () => {
    const { campaign, stage, channel, language, destination } = this.state;
    const body = {
      revision: campaign.payload.revision,
      stage: STAGES.indexOf(stage),
      channel,
      language,
      request_id: uuid()
    };
    if (channel === "email") body.email = destination.trim();
    if (channel === "sms") body.phone = destination.trim();
    if (channel === "push") body.mobile_no = destination.trim();
    this.mutate(campaignApi.sendTest, [campaign.id, body], { test: body, sendsMessages: true });
  };

  handleRetryTest = () => {
    const { campaign, uncertain } = this.state;
    if (!uncertain) return;
    this.mutate(campaignApi.sendTest, [campaign.id, uncertain], { test: uncertain, sendsMessages: true });
  };

  handleApprove = () => {
    const { campaign, reviewed, received } = this.state;
    const { deliveryChannels, deliveryLanguages } = this.getDerived();
    const body = { revision: campaign.payload.revision, content_reviewed: reviewed, test_received: received, delivery_channels: deliveryChannels };
    if (campaign.payload.settings.language_mode === "explicit") body.delivery_languages = deliveryLanguages;
    this.mutate(
      campaignApi.approve,
      [campaign.id, body],
      // Overlong SMS only blocks approval when SMS is actually going out.
      { sendsMessages: deliveryChannels.includes("sms") }
    );
  };

  handleActivate = () => {
    const { campaign } = this.state;
    this.setState({
      confirm: {
        ...getConfirmation("activate", { revision: campaign.payload.revision, audienceCount: campaign.payload.audience_count }),
        run: () => this.mutate(campaignApi.activate, [campaign.id, { revision: campaign.payload.revision }], { sendsMessages: true })
      }
    });
  };

  handlePause = () => this.setState({
    confirm: {
      ...getConfirmation("pause"),
      run: () => this.mutate(campaignApi.pause, [this.state.campaign.id])
    }
  });

  handleResume = () => this.mutate(campaignApi.resume, [this.state.campaign.id], { sendsMessages: true });

  handleRetryAudience = () => this.mutate(campaignApi.retryAudience, [this.state.campaign.id]);

  handleConfirm = () => {
    const { confirm } = this.state;
    this.setState({ confirm: null }, confirm && confirm.run);
  };

  viewHandlers = {
    onBack: () => this.props.history.push(this.props.basePath),
    onRefresh: () => { this.outcomesFetchedAt = 0; this.refresh(); },
    onSettingsChange: (key, value) => this.handleSettingsChange(key, value),
    onFeedbackChange: feedback => this.setState({ feedback }),
    onGenerate: () => this.handleGenerate(),
    onRetryGeneration: () => this.handleRetryGeneration(),
    onDiscard: () => this.handleDiscard(),
    onEdit: (key, value) => this.handleEdit(key, value),
    onStageChange: stage => {
      const index = STAGES.indexOf(stage);
      this.setState({ stage, channel: STAGE_CHANNELS[index], destination: "", testFieldError: "" });
    },
    onChannelChange: channel => this.setState({ channel, destination: "", testFieldError: "" }),
    onLanguageChange: language => this.setState({ language }),
    onLeadChange: lead => this.setState({ lead, preview: null }),
    onPreview: () => this.handlePreview(),
    onDestinationChange: destination => this.setState({ destination, testFieldError: "" }),
    onReviewedChange: reviewed => this.setState({ reviewed }),
    onReceivedChange: received => this.setState({ received }),
    onDeliveryChannelsChange: deliveryChannels => this.setState({ deliveryChannels }),
    onDeliveryLanguagesChange: deliveryLanguages => this.setState({ deliveryLanguages }),
    onAudiencePage: page => this.loadPage("audience", page),
    onAudienceListChange: (list, day) => this.handleAudienceListChange(list, day),
    onOutcomeWindowChange: days => this.handleOutcomeWindowChange(days),
    onRetryOutcomes: () => this.retryOutcomes(),
    onLogsPage: page => this.loadPage("logs", page),
    onSendTest: () => this.handleSendTest(),
    onRetryTest: () => this.handleRetryTest()
  };

  getDerived() {
    const state = this.state;
    const { campaign, workspace } = state;
    const payload = campaign.payload;
    const configuration = workspace.configuration;
    const smsErrors = overlongSms(state.content, state.settings);
    const savedSettings = payload.settings || emptySettings();
    const settingsChanged = JSON.stringify(state.settings) !== JSON.stringify(savedSettings);
    const automated = !!payload.automation;
    const canEdit = !automated && ["draft", "approved"].includes(campaign.status) && !state.busy && !state.stale;
    const canUseSaved = smsErrors.length === 0 && !state.dirty && !state.busy && !state.stale &&
      hasCompleteContent(payload) && campaign.status !== "generating";
    const channelCount = savedSettings.channels.length;
    const allTested = allChannelsTested(savedSettings, state.tested);
    const explicitLanguages = savedSettings.language_mode === "explicit";
    // A single generated option has nothing to choose, so it is always sent.
    const pick = (generated, chosen) => (generated.length === 1 ? generated : chosen.filter(item => generated.includes(item)));
    const deliveryChannels = pick(savedSettings.channels, state.deliveryChannels);
    const deliveryLanguages = explicitLanguages ? pick(savedSettings.languages, state.deliveryLanguages) : [];

    return {
      campaign,
      configuration,
      catalogue: workspace.audience_catalogue || {},
      smsErrors,
      settingsChanged,
      dirty: state.dirty,
      stale: state.stale,
      uncertain: state.uncertain,
      allTested,
      canEdit,
      canUseSaved,
      canGenerate: canEdit && !!configuration.ai_ready && !!payload.audience_ready && state.settings.channels.length > 0 &&
        !(state.settings.language_mode === "explicit" && !state.settings.languages.length),
      canRetryGeneration: canEdit && !state.dirty && !!configuration.ai_ready &&
        !!payload.generation_id && (payload.progress && payload.progress.steps || []).some(step => step.status === "failed"),
      canSave: canEdit && state.dirty && !settingsChanged,
      canSendTest: canUseSaved && !!configuration.test_enabled && !!state.destination.trim() && !state.uncertain &&
        ((state.channel === "email" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.destination.trim())) ||
          (["sms", "push"].includes(state.channel) && isSriLankanMobile(state.destination))),
      // Tests are optional; the backend remains authoritative on approval.
      deliveryChannels,
      deliveryLanguages,
      canApprove: canUseSaved && canEdit && campaign.status === "draft" && state.reviewed && channelCount > 0 &&
        deliveryChannels.length > 0 && (!explicitLanguages || deliveryLanguages.length > 0),
      canActivate: canUseSaved && campaign.status === "approved" && !!configuration.send_enabled &&
        !!configuration.consent_confirmed && !!payload.audience_count,
      canResume: smsErrors.length === 0 && !!configuration.send_enabled && !!configuration.consent_confirmed
    };
  }

  render() {
    const state = this.state;
    const ready = state.campaign && state.workspace;
    let derived = null;
    let currentStep = null;
    let action = null;

    if (ready) {
      derived = this.getDerived();
      currentStep = getCurrentCampaignStep({
        campaign: state.campaign,
        tested: state.tested,
        dirty: state.dirty,
        audienceConfirmed: state.audienceConfirmed,
        contentConfirmedRevision: state.contentConfirmedRevision,
        testStepDoneRevision: state.testStepDoneRevision
      });
      action = getCampaignAction({
        currentStep,
        derived,
        handlers: {
          onConfirmAudience: this.handleConfirmAudience,
          onRetryAudience: this.handleRetryAudience,
          onGenerate: this.handleGenerate,
          onRetryGeneration: this.handleRetryGeneration,
          onSave: this.handleSave,
          onConfirmContent: this.handleConfirmContent,
          onFinishTesting: this.handleFinishTesting,
          onSendTest: this.handleSendTest,
          onRetryTest: this.handleRetryTest,
          onApprove: this.handleApprove,
          onActivate: this.handleActivate,
          onPause: this.handlePause,
          onResume: this.handleResume
        }
      });
    }

    return (
      <React.Fragment>
        <CampaignDetails
          model={state}
          derived={derived}
          currentStep={currentStep}
          action={action}
          handlers={this.viewHandlers}
        />
        <ConfirmModal
          confirm={state.confirm}
          isPending={state.busy}
          onCancel={() => this.setState({ confirm: null })}
          onConfirm={this.handleConfirm}
        />
      </React.Fragment>
    );
  }
}
